import type { OutboundDeliveryOrderDto } from '@nest/domain-entities-metadata-generated';
import { Skeleton } from '@nest/uikit-react';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function OutboundQueueDetailPage() {
	const client = useTerminalClient();
	const navigate = useNavigate();
	const queueId = useNumberParam('queueId');
	const documentTypeId = useNumberParam('documentTypeId');

	const { data: queue } = useQuery({
		queryKey: ['outbound-queue', queueId],
		queryFn: () => client.outboundQueues.getById.execute({ id: queueId! }),
		enabled: !!queueId
	});

	const { data: orders } = useQuery({
		queryKey: ['outbound-delivery-orders-by-queue', queueId],
		queryFn: () => client.outboundDeliveryOrders.getAllOutboundDeliveryOrdersByQueueQuery.execute({ queueId: queueId! }),
		enabled: !!queueId
	});

	const goToOrderDetail = useCallback(
		(orderId: number) => {
			navigate(`/outbound/delivery/orders/${orderId}?documentTypeId=${documentTypeId}&queueId=${queueId}`);
		},
		[navigate, documentTypeId, queueId]
	);

	return (
		<TerminalScreen showBackButton backButtonTo={`/outbound/${documentTypeId}/queues`} topBar={<div>{queue?.name}</div>}>
			<div className="flex flex-col gap-2 p-2">
				{orders ? (
					orders.length === 0 ? (
						<div className="p-6 text-center text-muted-foreground">Žádné doklady</div>
					) : (
						orders.map(order => <OutboundOrderRow key={order.id} order={order} onOpen={() => goToOrderDetail(order.id)} />)
					)
				) : (
					<>
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
					</>
				)}
			</div>
		</TerminalScreen>
	);
}

const OutboundOrderRow = React.memo(({ order, onOpen }: { order: OutboundDeliveryOrderDto; onOpen: () => void }) => {
	const buyerName = order.buyerSnapshot?.name ?? order.buyer?.name ?? '';
	const totalPieces = order.itemsCount ?? order.totalExpectedQuantity ?? order.lines?.length ?? 0;
	const isHighlighted = order.state === 'InProgress';
	const assignedTo = order.assignedTo?.name ?? null;

	return (
		<div onClick={onOpen} className={`w-full border border-border rounded-md transition-colors ${isHighlighted ? 'hover:bg-yellow-50' : 'hover:bg-slate-50'}`}>
			<div className={`bg-card border border-border rounded-md p-2 gap-2 ${isHighlighted ? 'bg-yellow-50 border-yellow-200' : ''}`}>
				<div className="flex flex-col text-left">
					<span className="text-muted-foreground">Odběratel</span>
					<span className="text-base font-semibold">{buyerName}</span>
				</div>
				<div className="flex flex-row justify-between">
					<div className="flex flex-col ">
						<span className="text-muted-foreground">Doklad</span>
						<span className="text-base font-semibold text-blue-600">{order.documentNumber}</span>
					</div>
					{order.foreignOrderNumber && (
						<div className="flex flex-col">
							<span className="text-muted-foreground">Cizí číslo</span>
							<span className="text-sm font-medium text-purple-600">{order.foreignOrderNumber}</span>
						</div>
					)}
				</div>
				<div className="flex flex-row justify-between">
					<div className="flex flex-col">
						<span className="text-muted-foreground">Kusů</span>
						<span className="font-semibold text-slate-700">{totalPieces}ks</span>
					</div>
					{assignedTo && (
						<div className="flex flex-col">
							<span className="text-muted-foreground">Přiřazeno</span>
							<span className="text-sm font-medium text-orange-600">{assignedTo}</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
});
